import {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  NotificationChannel,
  CreateChannelRequest,
  TestNotificationRequest,
  MonitorRule,
  CreateMonitorRequest,
  ChainInfo,
  ApiResponse,
  PaginatedResponse,
  SendNotificationRequest,
  BatchNotificationRequest,
  MonitorStats,
  User,
  Web3AuthRequest,
  Web3MessageResponse
} from '../types/auth';

/**
 * DDDDAlert API 客户端类
 * 封装与后端API的所有交互
 */
export class DDDDAlertAPI {
  private baseURL: string;
  private token: string | null = null;
  private apiKey: string | null = null;

  constructor(baseURL: string = 'https://api.519312.xyz') {
    this.baseURL = baseURL;
  }

  /**
   * 设置JWT Token
   */
  setToken(token: string) {
    this.token = token;
  }

  /**
   * 设置API Key
   */
  setApiKey(apiKey: string) {
    this.apiKey = apiKey;
  }

  /**
   * 通用请求方法
   */
  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    // 优先使用options中的Authorization，然后是token，最后是apiKey
    const optionsHeaders = options.headers as Record<string, string> | undefined;
    const authHeader = optionsHeaders?.['Authorization'] ||
                      (this.token ? `Bearer ${this.token}` :
                       this.apiKey ? `Bearer ${this.apiKey}` : undefined);

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(authHeader && { 'Authorization': authHeader }),
        ...options.headers
      },
      ...options
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  }

  // ==================== 认证相关 API ====================

  /**
   * 用户注册
   */
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const result = await this.request<AuthResponse['data']>('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });

    if (result.success && result.data) {
      this.setToken(result.data.token);
    }

    return result as AuthResponse;
  }

  /**
   * 用户登录
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const result = await this.request<AuthResponse['data']>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });

    if (result.success && result.data) {
      this.setToken(result.data.token);
    }

    return result as AuthResponse;
  }

  // ==================== Web3认证相关 API ====================

  /**
   * 获取Web3签名消息
   */
  async getWeb3Message(address: string): Promise<ApiResponse<Web3MessageResponse>> {
    return this.request<Web3MessageResponse>(`/api/v1/auth/web3/message?address=${address}`);
  }

  /**
   * Web3钱包注册
   */
  async web3Register(data: Web3AuthRequest): Promise<AuthResponse> {
    const result = await this.request<AuthResponse['data']>('/api/v1/auth/web3/register', {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (result.success && result.data) {
      this.setToken(result.data.token);
    }

    return result as AuthResponse;
  }

  /**
   * Web3钱包登录
   */
  async web3Login(data: Web3AuthRequest): Promise<AuthResponse> {
    const result = await this.request<AuthResponse['data']>('/api/v1/auth/web3/login', {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (result.success && result.data) {
      this.setToken(result.data.token);
    }

    return result as AuthResponse;
  }

  /**
   * 获取用户信息
   */
  async getProfile(): Promise<ApiResponse<User>> {
    return this.request<User>('/api/v1/auth/profile');
  }

  /**
   * 刷新API密钥
   */
  async refreshApiKey(): Promise<ApiResponse<{ api_key: string }>> {
    return this.request<{ api_key: string }>('/api/v1/auth/refresh-api-key', {
      method: 'POST'
    });
  }

  // ==================== 通知渠道管理 API ====================

  /**
   * 获取通知渠道列表
   */
  async getChannels(): Promise<PaginatedResponse<NotificationChannel>> {
    return this.request<NotificationChannel[]>('/api/v1/channels');
  }

  /**
   * 创建通知渠道
   */
  async createChannel(channelData: CreateChannelRequest): Promise<ApiResponse<NotificationChannel>> {
    return this.request<NotificationChannel>('/api/v1/channels', {
      method: 'POST',
      body: JSON.stringify(channelData)
    });
  }

  /**
   * 更新通知渠道
   */
  async updateChannel(
    channelId: string, 
    updateData: Partial<CreateChannelRequest & { is_enabled: boolean }>
  ): Promise<ApiResponse<NotificationChannel>> {
    return this.request<NotificationChannel>(`/api/v1/channels/${channelId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }

  /**
   * 删除通知渠道
   */
  async deleteChannel(channelId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/v1/channels/${channelId}`, {
      method: 'DELETE'
    });
  }

  /**
   * 测试通知渠道
   */
  async testChannel(channelId: string, message: TestNotificationRequest): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/v1/channels/${channelId}/test`, {
      method: 'POST',
      body: JSON.stringify(message)
    });
  }

  // ==================== 监控规则管理 API ====================

  /**
   * 获取监控规则列表
   */
  async getMonitors(params: {
    rule_type?: string;
    chain_id?: number;
    is_active?: boolean;
    page?: number;
    limit?: number;
  } = {}): Promise<PaginatedResponse<MonitorRule>> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        query.append(key, value.toString());
      }
    });

    const endpoint = `/api/v1/monitors${query.toString() ? '?' + query.toString() : ''}`;
    return this.request<MonitorRule[]>(endpoint);
  }

  /**
   * 创建监控规则
   */
  async createMonitor(monitorData: CreateMonitorRequest): Promise<ApiResponse<MonitorRule>> {
    return this.request<MonitorRule>('/api/v1/monitors', {
      method: 'POST',
      body: JSON.stringify(monitorData)
    });
  }

  /**
   * 更新监控规则
   */
  async updateMonitor(
    monitorId: string, 
    updateData: Partial<CreateMonitorRequest>
  ): Promise<ApiResponse<MonitorRule>> {
    return this.request<MonitorRule>(`/api/v1/monitors/${monitorId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }

  /**
   * 删除监控规则
   */
  async deleteMonitor(monitorId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/v1/monitors/${monitorId}`, {
      method: 'DELETE'
    });
  }

  /**
   * 切换监控规则状态
   */
  async toggleMonitor(monitorId: string): Promise<ApiResponse<MonitorRule>> {
    return this.request<MonitorRule>(`/api/v1/monitors/${monitorId}/toggle`, {
      method: 'POST'
    });
  }

  /**
   * 获取支持的区块链网络
   */
  async getSupportedChains(): Promise<ApiResponse<ChainInfo[]>> {
    return this.request<ChainInfo[]>('/api/v1/monitors/chains');
  }

  /**
   * 获取监控统计信息
   */
  async getMonitorStats(): Promise<ApiResponse<MonitorStats>> {
    return this.request<MonitorStats>('/api/v1/monitors/stats');
  }

  // ==================== 外部通知 API ====================

  /**
   * 发送单个通知
   */
  async sendNotification(
    channelId: string, 
    notification: SendNotificationRequest
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/v1/notify/${channelId}`, {
      method: 'POST',
      body: JSON.stringify(notification),
      headers: {
        'Authorization': `Bearer ${this.apiKey}`
      }
    });
  }

  /**
   * 批量发送通知
   */
  async sendBatchNotification(notification: BatchNotificationRequest): Promise<ApiResponse<void>> {
    return this.request<void>('/api/v1/notify/batch', {
      method: 'POST',
      body: JSON.stringify(notification),
      headers: {
        'Authorization': `Bearer ${this.apiKey}`
      }
    });
  }

  /**
   * 获取可用通知渠道（用于外部API）
   */
  async getNotifyChannels(): Promise<ApiResponse<NotificationChannel[]>> {
    return this.request<NotificationChannel[]>('/api/v1/notify/channels', {
      headers: {
        'Authorization': `Bearer ${this.apiKey}`
      }
    });
  }

  /**
   * 发送测试通知（用于外部API）
   */
  async sendTestNotification(channelId: string): Promise<ApiResponse<void>> {
    return this.request<void>('/api/v1/notify/test', {
      method: 'POST',
      body: JSON.stringify({ channel_id: channelId }),
      headers: {
        'Authorization': `Bearer ${this.apiKey}`
      }
    });
  }

  // ==================== 系统状态 API ====================

  /**
   * 健康检查
   */
  async healthCheck(): Promise<ApiResponse<{ status: string; timestamp: string; service: string }>> {
    return this.request<{ status: string; timestamp: string; service: string }>('/health');
  }
}

// 创建默认实例
export const ddddAlertApi = new DDDDAlertAPI();
